// Beat→measure grouping + measure normalization. Ported verbatim from poc/web/app.js (pure).

export interface RawBeat { time: number; next: number }

// Group beat times into measures using downbeats as bar boundaries.
export function beatsToMeasures(beats: number[], downbeats: number[], end: number): RawBeat[][] {
  const isDown = (t: number) => downbeats.some((d) => Math.abs(d - t) < 0.03);
  const measures: RawBeat[][] = [];
  let cur: RawBeat[] = [];
  for (let i = 0; i < beats.length; i++) {
    if (isDown(beats[i]) && cur.length) { measures.push(cur); cur = []; }
    cur.push({ time: beats[i], next: i + 1 < beats.length ? beats[i + 1] : end });
  }
  if (cur.length) measures.push(cur);
  return measures;
}

// Fix metrical artifacts: split over-long bars (clean multiples of perBar), re-grid runt runs
// (…3,1,3,1…), and re-grid uniformly when downbeats are pervasively short. Anchored on the first
// downbeat so a leading pickup (못갖춘마디) stays a short bar.
export function normalizeMeasures(
  measuresIn: RawBeat[][], beats: number[], end: number, downbeats: number[],
): RawBeat[][] {
  let measures = measuresIn;
  if (!measures.length || !beats || beats.length < 4) return measures;
  const counts: Record<number, number> = {};
  measures.forEach((m) => { if (m.length >= 3) counts[m.length] = (counts[m.length] || 0) + 1; });
  let perBar = 0, best = 0;
  for (const k in counts) if (counts[k] > best) { best = counts[k]; perBar = +k; }
  // SPLIT over-long bars (exact integer multiples of perBar)
  if (perBar >= 3) {
    const split: RawBeat[][] = [];
    measures.forEach((m) => {
      if (m.length >= 2 * perBar && m.length % perBar === 0)
        for (let i = 0; i < m.length; i += perBar) split.push(m.slice(i, i + perBar));
      else split.push(m);
    });
    measures = split;
  }
  // REGRID runt runs (≥2 consecutive off-size bars); isolated odd bars left untouched
  if (perBar >= 3) {
    const fixed: RawBeat[][] = [];
    let i = 0;
    while (i < measures.length) {
      if (measures[i].length === perBar) { fixed.push(measures[i]); i++; continue; }
      let j = i;
      while (j < measures.length && measures[j].length !== perBar) j++;
      if (j - i >= 2) {
        const flat: RawBeat[] = [];
        for (let k = i; k < j; k++) flat.push(...measures[k]);
        for (let k = 0; k < flat.length; k += perBar) fixed.push(flat.slice(k, k + perBar));
      } else {
        fixed.push(measures[i]);
      }
      i = j;
    }
    measures = fixed;
  }
  const shortCount = measures.filter((m) => m.length <= 2).length;
  if (!perBar || shortCount < measures.length * 0.15) return measures;
  const nextOf = (i: number) => (i + 1 < beats.length ? beats[i + 1] : end);
  const anchorTime = (downbeats && downbeats.length) ? downbeats[0] : measures[0][0].time;
  let a = 0, bd = Infinity;
  for (let i = 0; i < beats.length; i++) { const dd = Math.abs(beats[i] - anchorTime); if (dd < bd) { bd = dd; a = i; } }
  const out: RawBeat[][] = [];
  if (a > 0) out.push(beats.slice(0, a).map((t, j) => ({ time: t, next: nextOf(j) })));
  for (let i = a; i < beats.length; i += perBar) {
    const grp: RawBeat[] = [];
    for (let j = i; j < Math.min(i + perBar, beats.length); j++) grp.push({ time: beats[j], next: nextOf(j) });
    if (grp.length) out.push(grp);
  }
  return out;
}
